import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.VUE_APP_SUPABASE_URL
const supabaseKey = process.env.VUE_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
})

// The service_role JWT must never ship in the browser bundle (Vue inlines VUE_APP_* at build time).
// All access uses the anon key plus SECURITY DEFINER RPCs and storage policies — see
// Frontend/supabase/security_migration_remove_service_role_from_browser.sql

export class SupabaseFileService {
  
  /**
   * Create a file record in the database
   * @param {string} userId - ID of the person uploading the file
   * @param {string} residentId - ID of the resident who owns the document (can be different from userId)
   * @param {string} companyId - ID of the company who owns the document (can be different from userId)
   */
  static async createFileRecord({
    filename,
    filePath,
    fileSize,
    mimeType,
    lastModified,
    documentType,
    metadata = {},
    results = {},
    isValid = false,
    base64Data = null,
    userId = null,
    residentId = null,
    companyId = null,
    partnerId = null,
    pendingLinkType = null,
    tempReference = null,
    uploadedBy = null,
    template_id = null
  }) {
    const rpcParams = {
      p_filename: filename,
      p_file_path: filePath,
      p_file_size: fileSize,
      p_mime_type: mimeType,
      p_last_modified: lastModified,
      p_document_type: documentType,
      p_metadata: metadata,
      p_results: results,
      p_is_valid: isValid,
      p_base64_data: base64Data,
      p_user_id: userId,
      p_resident_id: residentId,
      p_company_id: companyId,
      p_pending_link_type: pendingLinkType,
      p_temp_reference: tempReference,
      p_uploaded_by: uploadedBy,
      p_partner_id: partnerId != null ? String(partnerId) : null,
      p_template_id: template_id != null ? String(template_id) : null
    }

    const { data, error } = await supabase.rpc('create_file_record', rpcParams)

    if (error) throw error
    return data
  }

  /**
   * Upload file to Supabase storage
   */
  static async uploadFileToStorage(file, documentType) {
    try {
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const fileExtension = file.name.split('.').pop()
      const filePath = `${documentType}/${timestamp}_${randomString}.${fileExtension}`

      const { data, error } = await supabase.storage
        .from('parkking-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) throw error

      return data.path
    } catch (error) {
      console.error('Error uploading file to storage:', error)
      throw error
    }
  }

  /**
   * Link files to a vehicle
   */
  static async linkFilesToVehicle(vehicleId, tempReference = null, fileIds = null) {
    const processedFileIds = fileIds && fileIds.length
      ? fileIds.map((id) => String(id))
      : null

    const { data, error } = await supabase
      .rpc('link_files_to_vehicle', {
        p_vehicle_id: vehicleId,
        p_temp_reference: tempReference,
        p_file_ids: processedFileIds
      })

    if (error) throw error
    return data
  }

  /**
   * Link files to a permit
   */
  static async linkFilesToPermit(permitId, tempReference = null, fileIds = null) {
    const processedFileIds = fileIds && fileIds.length
      ? fileIds.map((id) => String(id))
      : null

    const permitNumeric = typeof permitId === 'number' && !Number.isNaN(permitId)
      ? permitId
      : parseInt(String(permitId), 10)

    const { data, error } = await supabase
      .rpc('link_files_to_permit', {
        p_permit_id: Number.isNaN(permitNumeric) ? permitId : permitNumeric,
        p_temp_reference: tempReference,
        p_file_ids: processedFileIds
      })

    if (error) throw error
    return data
  }

  /**
   * Clear temp reference for files
   */
  static async clearTempReference(tempReference) {
    const { error } = await supabase.rpc('clear_temp_reference_for_files', {
      p_temp_reference: tempReference
    })

    if (error) throw error
    return true
  }

  /**
   * Generate a signed URL for file download
   */
  static async getSignedUrl(filePath, expiresIn = 3600) {
    const { data, error } = await supabase.storage
      .from('parkking-documents')
      .createSignedUrl(filePath, expiresIn)

    if (error) throw error
    return data.signedUrl
  }

  /**
   * Search files by various criteria (server-side RPC; at least one filter required)
   */
  static async searchFiles({
    documentType = null,
    permitId = null,
    templateId = null,
    userId = null,
    vehicleId = null,
    residentId = null,
    companyId = null,
    licensePlate = null,
    personName = null,
    companyName = null,
    isValid = null,
    dateFrom = null,
    dateTo = null,
    limit = 50,
    offset = 0
  } = {}) {
    const hasScope =
      (documentType != null && documentType !== '') ||
      (permitId != null && permitId !== '') ||
      (templateId != null && templateId !== '') ||
      (userId != null && userId !== '') ||
      (vehicleId != null && vehicleId !== '') ||
      (residentId != null && residentId !== '') ||
      (companyId != null && companyId !== '') ||
      (licensePlate != null && licensePlate !== '') ||
      (personName != null && personName !== '') ||
      (companyName != null && companyName !== '') ||
      (isValid !== null && isValid !== undefined) ||
      dateFrom != null ||
      dateTo != null

    if (!hasScope) {
      console.warn('searchFiles: at least one filter is required; returning no rows.')
      return []
    }

    if (licensePlate || personName || companyName) {
      console.warn('searchFiles: licensePlate, personName, and companyName filters use metadata ILIKE in the RPC.')
    }

    const { data, error } = await supabase.rpc('search_files', {
      p_document_type: documentType,
      p_permit_id: permitId != null && permitId !== '' ? String(permitId) : null,
      p_template_id: templateId != null && templateId !== '' ? String(templateId) : null,
      p_user_id: userId != null && userId !== '' ? String(userId) : null,
      p_vehicle_id: vehicleId != null && vehicleId !== '' ? String(vehicleId) : null,
      p_resident_id: residentId != null && residentId !== '' ? String(residentId) : null,
      p_company_id: companyId != null && companyId !== '' ? String(companyId) : null,
      p_license_plate: licensePlate,
      p_person_name: personName,
      p_company_name: companyName,
      p_is_valid: isValid !== null && isValid !== undefined ? isValid : null,
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_limit: limit,
      p_offset: offset
    })

    if (error) throw error
    return data || []
  }

  /**
   * Get unlinked files for a temporary reference
   */
  static async getUnlinkedFiles(tempReference, pendingLinkType = null) {
    const { data, error } = await supabase
      .rpc('get_unlinked_files', {
        p_temp_reference: tempReference,
        p_pending_link_type: pendingLinkType
      })

    if (error) throw error
    return data
  }

  /**
   * Get file download URL from storage
   */
  static async getFileDownloadUrl(filePath) {
    let normalized = filePath || ''
    if (normalized.startsWith('/')) normalized = normalized.replace(/^\/+/, '')
    if (normalized.startsWith('parkking-documents/')) {
      normalized = normalized.replace(/^parkking-documents\//, '')
    }

    const { data, error } = await supabase.storage
      .from('parkking-documents')
      .createSignedUrl(normalized, 3600)

    if (error) throw error
    return data.signedUrl
  }

  /**
   * Get a file by its ID
   */
  static async getFileById(fileId) {
    const { data, error } = await supabase.rpc('get_file_by_id', {
      p_file_id: String(fileId)
    })
    if (error) throw error
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null
    }
    return data || null
  }

  /**
   * Get a signed download URL for a given file ID
   */
  static async getSignedUrlByFileId(fileId) {
    if (typeof fileId === 'string' && fileId.includes('/')) {
      return this.getFileDownloadUrl(fileId)
    }

    let id = fileId
    if (typeof id === 'string' && /^\d+$/.test(id)) {
      id = parseInt(id, 10)
    }

    const file = await this.getFileById(id)
    if (file) {
      const path = file.filePath || file.file_path
      if (path) {
        return this.getFileDownloadUrl(path)
      }
    }

    if (typeof fileId === 'string') {
      return this.getFileDownloadUrl(fileId)
    }

    throw new Error('File not found')
  }

  /**
   * Delete file from storage and database
   */
  static async deleteFile(fileId, filePath) {
    try {
      const { error: storageError } = await supabase.storage
        .from('parkking-documents')
        .remove([filePath])

      if (storageError) throw storageError

      const { error: dbError } = await supabase.rpc('delete_file_row', {
        p_file_id: String(fileId)
      })

      if (dbError) throw dbError

      return true
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Update file metadata or relationships
   */
  static async updateFile(fileId, updates) {
    const { data, error } = await supabase.rpc('update_file_patch', {
      p_file_id: String(fileId),
      p_patch: updates
    })

    if (error) throw error
    if (Array.isArray(data) && data.length > 0) return data[0]
    return data
  }

  /**
   * Get files for a specific vehicle
   */
  static async getVehicleFiles(vehicleId) {
    return this.searchFiles({ vehicleId })
  }

  /**
   * Get files for a specific resident
   */
  static async getResidentFiles(residentId) {
    return this.searchFiles({ residentId })
  }

  /**
   * Get files by document type
   */
  static async getFilesByDocumentType(documentType, filters = {}) {
    return this.searchFiles({ documentType, ...filters })
  }

  /**
   * Upload file on behalf of a resident (third-party upload)
   */
  static async uploadForResident({
    file,
    documentType,
    residentId,
    uploaderId,
    uploaderName,
    metadata = {},
    tempReference = null
  }) {
    try {
      const filePath = await this.uploadFileToStorage(file, documentType)

      const fileId = await this.createFileRecord({
        filename: file.name,
        filePath,
        fileSize: file.size,
        mimeType: file.type,
        lastModified: file.lastModified,
        documentType,
        metadata,
        userId: uploaderId,
        residentId: residentId,
        pendingLinkType: 'vehicle',
        tempReference,
        uploadedBy: uploaderName
      })

      return { fileId, filePath }
    } catch (error) {
      console.error('Error uploading file for resident:', error)
      throw error
    }
  }

  /**
   * Upload file on behalf of a company (third-party upload)
   */
  static async uploadForCompany({
    file,
    documentType,
    companyId,
    uploaderId,
    uploaderName,
    metadata = {},
    tempReference = null
  }) {
    try {
      const filePath = await this.uploadFileToStorage(file, documentType)

      const fileId = await this.createFileRecord({
        filename: file.name,
        filePath,
        fileSize: file.size,
        mimeType: file.type,
        lastModified: file.lastModified,
        documentType,
        metadata,
        userId: uploaderId,
        companyId: companyId,
        pendingLinkType: 'vehicle',
        tempReference,
        uploadedBy: uploaderName
      })

      return { fileId, filePath }
    } catch (error) {
      console.error('Error uploading file for company:', error)
      throw error
    }
  }

  /**
   * Upload file for self (user uploads their own document)
   */
  static async uploadForSelf({
    file,
    documentType,
    userId,
    userName,
    metadata = {},
    tempReference = null
  }) {
    try {
      const filePath = await this.uploadFileToStorage(file, documentType)

      const fileId = await this.createFileRecord({
        filename: file.name,
        filePath,
        fileSize: file.size,
        mimeType: file.type,
        lastModified: file.lastModified,
        documentType,
        metadata,
        userId: userId,
        residentId: userId,
        pendingLinkType: 'vehicle',
        tempReference,
        uploadedBy: userName
      })

      return { fileId, filePath }
    } catch (error) {
      console.error('Error uploading file for self:', error)
      throw error
    }
  }

  /**
   * Get files for a specific permit
   */
  static async getPermitFiles(permitId) {
    return this.searchFiles({ permitId })
  }

  /**
   * Get files for a specific company
   */
  static async getCompanyFiles(companyId) {
    return this.searchFiles({ companyId })
  }

  /**
   * Import best streets from CSV files stored in Supabase storage
   * @deprecated Run from a private script with the service role — never from the browser.
   */
  static async importBestStreetsFromCsv() {
    return {
      success: false,
      errors: [
        'importBestStreetsFromCsv is disabled in the browser. Use a server-side script or Edge Function with the service role key.'
      ]
    }
  }
}

export default SupabaseFileService
